// Presentation for the candidate engagement score.
//
// The band comes from the backend rather than being derived from the number here, because the two
// are not interchangeable: `unknown` means the backend held too little signal to publish a score at
// all, which is a different statement from "scored zero". The old UI derived tone from the score
// alone, so a candidate we simply had no data on rendered as a red 0/10 — the worst-looking result
// on the page — next to a fully onboarded, actively matched profile.

const BANDS = {
	hot: { tone: 'success', label: 'Hot' },
	warm: { tone: 'success', label: 'Warm' },
	cooling: { tone: 'warning', label: 'Cooling' },
	cold: { tone: 'warning', label: 'Cold' },
	dormant: { tone: 'danger', label: 'Dormant' },
	do_not_contact: { tone: 'danger', label: 'Do not contact' },
	unknown: { tone: 'neutral', label: 'No signal yet' },
}

const UNKNOWN = BANDS.unknown

export const engagementBand = (band) => BANDS[band] || UNKNOWN

// A band the backend did not publish is the same statement as `unknown`, and on staging it is by
// far the common one: 4,003 of 4,008 worker_statistics rows carry engagement_band NULL beside
// engagement_score 0.0. Reading "score is present" off the number alone rendered those as
// "0.0/10 · No signal yet" — a score and a denial of the score in the same breath.
export const hasEngagementScore = (score, band) => score != null && !!BANDS[band] && band !== 'unknown'

// "6.5/10" reads as a measurement; "—" reads as a gap. Neither should ever be a red zero.
export const engagementValueLabel = (score, band) => (hasEngagementScore(score, band) ? `${Number(score).toFixed(1)}/10` : '—')

export const engagementTone = (score, band) => engagementBand(band).tone

export const engagementMeter = (score, band) => (hasEngagementScore(score, band) ? Number(score) * 10 : 0)

// Coverage is the share of the model's weight that had any reading at all. Saying so is what keeps a
// low score honest: 2.6 built on responsiveness alone is a different claim from 2.6 built on
// everything we track.
export const engagementCoverageHint = (band, coverage) => {
	if (band === 'unknown') return 'No engagement signal recorded for this candidate yet.'
	if (coverage == null) return undefined
	return `Based on ${coverage}% of the engagement model; the rest has no data for this candidate.`
}
